import React from 'react';

import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import { COLOURS, type Filters, build, format, pcmMetrics, sourceMetricChangeLabel } from './model';

const sectionHeading = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  py: 0.5,
  px: 1,
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
} as const;
const detailTable = {
  '& th, & td': { py: 0.5, px: 1, fontSize: 12, borderColor: 'divider' },
  '& th': { fontWeight: 600 },
  '& thead th': { bgcolor: 'action.hover', color: 'text.primary' },
  '& tbody tr:nth-of-type(even)': { bgcolor: 'action.hover' },
};

export default function PcmReport({
  model,
  filters,
  only,
}: {
  model: ReturnType<typeof build>;
  filters: Filters;
  only: boolean;
}) {
  const theme = useTheme();
  const value = (side: 'current' | 'comparison', metric: (typeof pcmMetrics)[number]) =>
    format(model[side].totals[metric.id], metric.unit === 'money' ? 'moneyValue' : metric.unit);
  return (
    <Stack gap={1} sx={{ px: 2, py: 1.5, maxWidth: 1800, width: '100%', mx: 'auto', minWidth: 0, overflow: 'visible' }}>
      {model.viewScopeUnknown && (
        <Alert severity="info">Offer views and attributed conversion are Unknown under room or package filters.</Alert>
      )}
      {model.currencyScopeDifferent && (
        <Alert severity="info">
          Sales use {filters.currency} rows; traffic retains all currencies. Gross margin is AUD.
        </Alert>
      )}
      <TableContainer tabIndex={0} role="region" aria-label="PCM performance comparison">
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            minWidth: 900,
            '& th, & td': { border: 3, borderColor: 'background.default', textAlign: 'center', p: 0.5 },
            '& thead th': { bgcolor: 'action.hover', color: 'text.primary', fontSize: 10, lineHeight: 1.2 },
            '& th:first-of-type': { width: 112 },
            '& tbody th': { bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 12 },
            '& td': { bgcolor: 'background.paper', fontWeight: 600, fontSize: 18 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{filters.refund}</TableCell>
              {pcmMetrics.map((metric) => (
                <TableCell key={metric.id}>
                  <Tooltip title={metric.note}>
                    <Box component="span" tabIndex={0}>
                      {metric.label.toUpperCase()}
                      {metric.unit === 'money' && (
                        <Box component="span" sx={{ display: 'block', fontSize: 9 }}>
                          AUD
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(['current', ...(!only ? ['comparison'] : [])] as Array<'current' | 'comparison'>).map((side) => (
              <TableRow key={side}>
                <TableCell component="th" scope="row">
                  {side === 'current' ? 'Current' : 'Comparison'}
                </TableCell>
                {pcmMetrics.map((metric) => (
                  <TableCell key={metric.id}>{value(side, metric)}</TableCell>
                ))}
              </TableRow>
            ))}
            {!only && (
              <TableRow sx={{ '& td': { fontSize: 12, color: 'primary.main' } }}>
                <TableCell component="th" scope="row">
                  Change
                </TableCell>
                {pcmMetrics.map((metric) => (
                  <TableCell key={metric.id}>
                    {sourceMetricChangeLabel(
                      model.current.totals[metric.id],
                      model.comparison.totals[metric.id],
                      metric.id,
                    )}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1.5fr' }, gap: 1 }}>
        <Box sx={{ bgcolor: 'background.paper', minWidth: 0 }}>
          <Typography component="h2" sx={sectionHeading}>
            Cumulative offer views
          </Typography>
          <Box sx={{ height: 310, px: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={model.curve} margin={{ top: 16, right: 20, bottom: 25 }}>
                <CartesianGrid vertical={false} stroke={theme.palette.divider} />
                <XAxis
                  dataKey="day"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 10 }}
                  label={{ value: 'Days since selected start', position: 'bottom', fontSize: 10 }}
                />
                <YAxis
                  width={60}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => new Intl.NumberFormat('en', { notation: 'compact' }).format(value)}
                />
                <ReportChartTooltip />
                <Line
                  dataKey="views"
                  name="Current offer"
                  stroke={COLOURS.current}
                  dot={false}
                  strokeWidth={2.5}
                  isAnimationActive={false}
                />
                {!only && (
                  <Line
                    dataKey="cmpViews"
                    name="Comparison offer"
                    stroke={COLOURS.comparison}
                    dot={false}
                    strokeDasharray="5 3"
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', pb: 1 }}>
            Current offer{!only && ' · Comparison offer (dashed)'}
          </Typography>
        </Box>
        <Box sx={{ bgcolor: 'background.paper', minWidth: 0 }}>
          <Typography component="h2" sx={sectionHeading}>
            Packages by room type
          </Typography>
          <TableContainer>
            <Table size="small" sx={detailTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Room type</TableCell>
                  <TableCell align="right">Current packages</TableCell>
                  {!only && <TableCell align="right">Comparison packages</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {model.rooms.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">{format(row.current.packages)}</TableCell>
                    {!only && <TableCell align="right">{format(row.comparison.packages)}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <Box sx={{ bgcolor: 'background.paper' }}>
        <Typography component="h2" sx={sectionHeading}>
          Offer views by traffic channel
        </Typography>
        <Table size="small" sx={detailTable}>
          <TableHead>
            <TableRow>
              <TableCell>Channel</TableCell>
              <TableCell align="right">Current views</TableCell>
              {!only && <TableCell align="right">Comparison views</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {model.channels.map((row) => (
              <TableRow key={row.name}>
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">{format(row.current)}</TableCell>
                {!only && <TableCell align="right">{format(row.comparison)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Sydney dates · {filters.from} – {filters.to} · Rate changes are percentage points; unavailable values remain
        Unknown. Hover or focus a measure for its definition.
      </Typography>
    </Stack>
  );
}
